import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface StaticPageLayoutProps {
  titleKey: string;
  subtitleKey: string;
  lastUpdatedKey?: string;
  date?: string;
  children: React.ReactNode;
}

/**
 * Shared layout for static/legal pages (About, Support, Privacy, Terms).
 * Provides consistent structure: back link, title, optional date, content area.
 */
export function StaticPageLayout({
  titleKey,
  subtitleKey,
  lastUpdatedKey,
  date,
  children,
}: StaticPageLayoutProps) {
  const { t } = useTranslation();

  return (
    <article className="py-8 md:py-12">
      <Link
        to="/"
        className="inline-flex items-center gap-2 font-body text-sm text-neutral-medium hover:text-brand-accent transition-colors mb-8"
      >
        <svg
          className="w-4 h-4 rtl:rotate-180"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
        {t('staticPages.backToHome')}
      </Link>

      <header className="mb-10">
        <h1 className="text-3xl md:text-4xl font-heading font-bold text-brand-accent mb-2">
          {t(titleKey)}
        </h1>
        <p className="text-lg text-neutral-medium font-body">{t(subtitleKey)}</p>
        {lastUpdatedKey && date && (
          <p className="mt-2 text-sm text-neutral-medium/80 font-body">
            {t(lastUpdatedKey)}: {date}
          </p>
        )}
      </header>

      <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-10 space-y-8 font-body text-neutral-light/90 leading-relaxed">
        {children}
      </div>
    </article>
  );
}
